<template>
    <header-index />
    <breadCrumb />

    <!-- Main Content -->
    <div class="container mx-auto px-4 py-8">
        <div class="gi-grid">
            <!-- Left: Article content -->
            <div class="gi-article">
                <article id="article">
                    <h1 class="gi-title">{{ article.title }}</h1>
                    <div class="gi-meta">
                        <nuxt-link to="https://www.leaflypackaging.com/authors"class="gi-meta-item"> Leafly Author Team </nuxt-link>
                        <span class="gi-meta-dot">·</span>
                        <time :datetime="article.createTime ? new Date(article.createTime).toISOString().split('T')[0] : ''" itemprop="datePublished">
                            {{ article.createTime ? (new Date(article.createTime).toLocaleDateString('en-CA')) : 'Updated just now' }}
                        </time>
                    </div>

                    <div v-if="heroImage" class="gi-hero">
                        <img :src="heroImage" :alt="article.title" loading="eager" />
                    </div>

                    <div class="article-content-detail" v-if="article.content" v-html="article.content"></div>
                </article>
                <div class="flex gap-1 mt-[24px]">
                    <p class="leading-[40px] font-bold text-[16px] text-[#059669]">Share:</p>
                    <SocialShare network="facebook" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share facebook"/>
                    <SocialShare network="linkedin" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share linkedin"/>
                    <SocialShare network="twitter" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share twitter"/>
                    <SocialShare network="whatsapp" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share whatsapp"/>
                    <SocialShare network="telegram" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share telegram"/>
                    <SocialShare network="pinterest" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share pinterest"/>
                    <SocialShare network="reddit" :url="currentUrl" :image="shareImageUrl" :styled="true" class="share-item" title="share reddit"/>
                </div>
            </div>

            <!-- Right: Sidebar -->
            <aside class="gi-sidebar">
                <div class=" category-wrapper">
                    <div ref="dropdownButton" class="category"
                        :class="{ 'show-dropdown': showDropdown, 'show-dropdown-btn': showDropdown }" @mouseenter="showDropdown = true">
                        <p>Product Category</p>
                        <p class="mt-[10px]">
                            <img src="/leaflypackaging-com/images/menu-list-icon.svg" width="30" height="30" alt="" />
                        </p>
                    </div>

                    <!-- 下拉菜单 -->
                    <div v-if="showDropdown" ref="dropdownList" :class="{ 'show-dropdown': showDropdown, 'show-dropdown-menu': showDropdown }"
                        class=" top-full left-0  w-100% bg-white border border-gray-200 rounded-md shadow-lg z-50">
                        <!-- 一级分类循环 -->
                        <div v-for="item in navItemList" :key="item.id" class="first-level-menu border-b border-gray-100 last:border-b-0">
                            <div class="first-level-item" @click="toggleMenuItem(item)">
                                <span class="first-level-text">{{ item.name }}</span>
                                <img src="/leaflypackaging-com/images/chevron-down.svg" class="arrow-icon" alt="" />
                            </div>

                            <!-- 二级分类 -->
                            <transition name="dropdown" @enter="enter" @leave="leave">
                                <div v-if="item.isOpen" class="submenu-container pl-6 pr-3  bg-gray-50">
                                    <div v-for="subCat in item.children" :key="subCat.id" class="second-level-menu py-1 hover:text-green-700 cursor-pointer">
                                        <nuxt-link :to="subCat.linkUrl"
                                            :class="route.path === subCat.linkUrl ? 'text-sm text-green-600 font-weight-600' : 'text-sm text-gray-700 hover:text-green-700 hover:font-weight-600'">
                                            {{ subCat.name }}
                                        </nuxt-link>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>

                <div class="gi-sidebar-box sticky-inquiry-box" ref="inquiryBoxRef">
                    <h3 class="gi-side-title">Package Customized Inquiry</h3>
                    <div class="gi-form">
                        <input v-model="formData.contactName" type="text" placeholder="Your Name" class="gi-theme-border" />
                        <input v-model="formData.email" type="email" placeholder="Email" class="gi-theme-border" />
                        <input v-model="formData.phoneNumber" type="text" placeholder="Phone" class="gi-theme-border" />
                        <textarea v-model="formData.message" rows="4" placeholder="Message" class="gi-theme-border"></textarea>
                        <button
                            class="rounded"
                            @click="submitQuery"
                            :disabled="submitting"
                            :class="submitting ? 'gi-btn-submitting' : ''"
                        >
                            {{ submitting ? 'Waiting...' : 'Get More Info' }}
                        </button>
                    </div>
                </div>
            </aside>
        </div>

        <!-- Bottom: Latest Products and Articles -->
        <div class="gi-bottom-full">
            <section class="gi-latest-products">
                <h2 class="gi-subtitle">Latest Products</h2>
                <div class="gi-products-grid">
                    <div v-for="(p, i) in latestProducts" :key="i" class="gi-product-card">
                        <div class="gi-product-img">
                            <img :src="p.imageUrl" :alt="p.title" loading="lazy" />
                        </div>
                        <div class="gi-product-info">
                            <nuxt-link :to="p.fullUrl" class="gi-article-title">{{ p.title }}</nuxt-link>
                            <p class="gi-product-desc">{{ p.description }}</p>
                            <nuxt-link :to="p.fullUrl" class="gi-product-more">Read More &gt;&gt;</nuxt-link>
                        </div>
                    </div>
                </div>
            </section>

            <section class="gi-latest-articles">
                <h2 class="gi-subtitle">Latest Articles</h2>
                <div class="gi-articles-list">
                    <div v-for="(a, i) in latestArticles" :key="i" class="gi-article-item">
                        <div class="gi-article-thumb">
                            <img :src="a.imageUrl" :alt="a.title" loading="lazy" />
                        </div>
                        <div class="gi-article-right">
                            <nuxt-link :to="a.fullUrl" class="gi-article-title">{{ a.title }}</nuxt-link>
                            <p class="gi-article-desc">{{ a.description }}</p>
                            <nuxt-link :to="a.fullUrl" class="gi-article-more">Read More &gt;&gt;</nuxt-link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
    <toast-message ref="toastMessageRef" />
    <footer-index />
</template>

<script setup lang="ts" name="ArticleGetInspired">
    import { ref, computed } from 'vue'
    import type { NavItemVo, Article, Category, Page, Response, ArticleListPo } from '~/types';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import toastMessage from '../../components/common/toastMessage.vue'
    import breadCrumb from '../../components/common/breadCrumb.vue'
    import { useHttpBy$Fetch, useHttp } from '~/utils/httpUtil'
    const cmsInfo = await cmsUtils.getCmsInfo()
    const props = defineProps<{
        article: Article,
        category: Category
    }>()

    // 下面是的处理是为了生成JSON-LD数据 
    const route = useRoute()
    const canonicalUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain + route.path.replace('.html','')
    useHead({
        script: [
            // ============================================
            // 1. Article 结构化数据
            // ============================================
            {
                type: 'application/ld+json',
                // 🔧 移到 body 末尾，减轻 head 负担
                tagPosition: 'bodyClose',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    '@id': canonicalUrl + '#article',
                    headline: props.article.title,
                    description: props.article.description,
                    keywords: props.article.keywords,
                    ...(props.article.imageUrl ? { image: props.article.imageUrl } : {}),
                    ...(props.article.createTime ? { datePublished: props.article.createTime } : {}),
                    ...(props.article.updateTime ? { dateModified: props.article.updateTime } : {}),
                    author: { '@id': '#organization' },
                    publisher: { '@id': '#organization' },
                    
                    // 🆕 新增属性
                    ...(props.article.content ? {
                        wordCount: props.article.content
                            .replace(/<[^>]*>/g, '')
                            .split(/\s+/)
                            .filter(w => w.length > 0).length
                    } : {}),
                    ...(props.category?.name ? {
                        articleSection: props.category.name
                    } : {}),
                    inLanguage: 'en',
                    isAccessibleForFree: true,
                    ...(props.article.content ? {
                        timeRequired: 'PT' + Math.max(1, Math.ceil(
                            props.article.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length / 250
                        )) + 'M'
                    } : {}),
                    
                    mainEntityOfPage: { 
                        '@type': 'WebPage', 
                        '@id': canonicalUrl 
                    },
                    url: canonicalUrl
                })
            },
            
            // ============================================
            // 2. BreadcrumbList 结构化数据 — 🔧 修复
            // ============================================
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        { 
                            '@type': 'ListItem', 
                            position: 1, 
                            name: 'Home', 
                            item: (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain + '/' 
                        },
                        // 🔧 修复：第2级指向分类列表页（不是文章自己）
                        ...(props.category?.url ? [{
                            '@type': 'ListItem',
                            position: 2,
                            name: props.category.name,
                            item: (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain + '/' + props.category.url + '/'
                        }] : []),
                        // 🆕 新增：第3级是当前文章标题
                        {
                            '@type': 'ListItem',
                            position: props.category?.url ? 3 : 2,
                            name: props.article.title,
                            item: canonicalUrl
                        }
                    ]
                })
            },
            
            // ============================================
            // 3. Organization 结构化数据 — 🔧 补充社交媒体
            // ============================================
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    '@id': '#organization',
                    name: cmsInfo.website.title,
                    url: (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain,
                    sameAs: [
                        'https://www.linkedin.com/company/leaflypackaging',
                        'https://www.youtube.com/@LeaflyPackaging',       // 🆕
                        'https://www.tiktok.com/@LeaflyPackaging',        // 🆕
                        'https://www.facebook.com/profile.php?id=61584007651167'  // 🆕
                    ]
                })
            }
        ]
    })

    // 获取文章的url  localhost/box/asf-sd-sdf.html
    const toastMessageRef = ref<InstanceType<typeof toastMessage> | null>(null)

    // 获取当前页面的完整 URL
    const currentUrl = computed(() => {
        if (import.meta.client) {
            // 客户端：使用 window.location.href
            return window.location.href
        } else {
            // 服务端：使用 useRequestURL 获取完整 URL
            const requestURL = useRequestURL()
            return requestURL.href
        }
    })

    // 获取文章图片 URL（用于社交分享）
    const shareImageUrl = computed(() => {
        if (!props.article.imageUrl) {
            return ''
        }
        // 如果已经是完整 URL，直接返回
        if (props.article.imageUrl.startsWith('http://') || props.article.imageUrl.startsWith('https://')) {
            return props.article.imageUrl
        }
        // 如果是相对路径，转换为绝对 URL
        if (import.meta.client) {
            return new URL(props.article.imageUrl, window.location.origin).href
        } else {
            const requestURL = useRequestURL()
            return new URL(props.article.imageUrl, requestURL.origin).href
        }
    })

    // hero image if exists
    const heroImage = ref<string | null>(props.article.contentImgs?.split(',')[0] || null)

    // ----------------------------这里是product category的代码 控制下拉菜单显示---------------------

    const navItemList = ref<NavItemVo[]>(cmsUtils.getNavItemTreeByNavIndex("index_category", cmsInfo) as NavItemVo[])
        const showDropdown = ref(true)
    const dropdownButton = ref<HTMLElement | null>(null)
    const dropdownList = ref<HTMLElement | null>(null)

    // 点击外部关闭下拉菜单
    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownButton.value && !dropdownButton.value.contains(event.target as Node) && dropdownList.value && !dropdownList.value.contains(event.target as Node)) {
            showDropdown.value = false
        }
    }

    // 手风琴效果：切换菜单项展开状态
    const toggleMenuItem = (clickedItem: any) => {
        // 先收起所有菜单项
        navItemList.value.forEach(item => {
            item.isOpen = false
        })
        // 然后展开点击的菜单项
        clickedItem.isOpen = true
    }
    // 动画进入函数
    const enter = (el: Element, done: () => void) => {
        const element = el as HTMLElement
        element.style.height = '0'
        element.style.overflow = 'hidden'
        element.offsetHeight // 强制重绘
        element.style.height = element.scrollHeight + 'px'
        setTimeout(done, 400) // 与CSS动画时长匹配
    }
    // 动画离开函数
    const leave = (el: Element, done: () => void) => {
        const element = el as HTMLElement
        element.style.height = element.scrollHeight + 'px'
        element.style.overflow = 'hidden'
        element.offsetHeight // 强制重绘
        element.style.height = '0'
        setTimeout(done, 400) // 与CSS动画时长匹配
    }
    // menu 根据url默认展开的是对应的菜单项
    watchEffect(() => {
        if (navItemList.value[0]) {
            navItemList.value[0].isOpen = true
        }
    })


    //-----------------------------------最新产品------------------------------------------------------
    const productCategoryIds = cmsUtils.getChildCategoryIds(8, cmsInfo);
    const allCategoryIds = [8, ...productCategoryIds];
    const articleListPo1 : ArticleListPo = {
        "websiteId": cmsInfo!.website.id,
        "categoryIds": allCategoryIds,
        "pageNum": 1,
        "pageSize": 12
    }

    const articlesPage = await useHttpBy$Fetch("/nuxt/article/list", {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: articleListPo1
    }) as Page<Article>;
    articlesPage.rows.forEach((article: Article) => {
        let articleCategory: Category = cmsInfo.category.find(item => item.id == article.categoryId)!;
        article.fullUrl = cmsUtils.appendCategoryUrl(articleCategory, cmsInfo, "/" + article.titleUrl );
    });
    // Latest sections (mock)
    const latestProducts = ref(articlesPage.rows)

    //----------------------------最新新闻-----------------------------------------------
    const blogCategoryIds = [49];
    const articleListPo2: ArticleListPo = {
        "websiteId": cmsInfo!.website.id,
        "categoryIds": blogCategoryIds,
        "pageNum": 1,
        "pageSize": 6
    }

    const blogPage = await useHttpBy$Fetch("/nuxt/article/list", {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: articleListPo2
    }) as Page<Article>;
    blogPage.rows.forEach((article: Article) => {
        let articleCategory: Category = cmsInfo.category.find(item => item.id == article.categoryId)!;
        article.fullUrl = cmsUtils.appendCategoryUrl(articleCategory, cmsInfo, "/" + article.titleUrl);
    });
    const latestArticles = ref(blogPage.rows)

    //----------------------------------咨询信息安全认证----------------------------------------------
    let res = await useHttpBy$Fetch("/nuxt/common/getTimestamp", {
        method: 'POST',
        body: {},
        // 使用FormData时不要设置Content-Type，浏览器会自动设置正确的类型
        headers: {
            // 移除Content-Type让浏览器自动设置
        }
    }) as Response<string>
    const timestampStr = res.data
    let randomStr = ''

    // 生成 Token
    async function generateToken(websiteIdStr: string, timestampStr: string) {
        let randomArray = new Uint8Array(16);
        window.crypto.getRandomValues(randomArray);
        randomStr = Array.from(randomArray).map(b => b.toString(16).padStart(2, '0')).join('');
        const data = `${websiteIdStr}:${timestampStr}:${randomStr}`;
        // 使用 Web Crypto API 计算哈希
        const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(data))
        const hashArray = Array.from(new Uint8Array(hash));
        const token = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return token;
    }
    const formData = ref({
        contactName: '',
        email: '',
        phoneNumber: '',
        message: ''
    })
    // 提交中状态：防止重复提交，按钮置灰 + 文案变为 Waiting...
    const submitting = ref(false)
    const submitQuery = async () => {
        // 防止重复提交
        if (submitting.value) return
        submitting.value = true

        // 创建FormData对象用于文件上传
        // 校验 email 格式
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.value.email)) {
            toastMessageRef.value?.showSuccess('Please enter a valid email address!')
            submitting.value = false
            return
        }
        // 校验手机号格式（只允许数字、+、(、) 和空格，与后端一致）
        if (formData.value.phoneNumber.trim() !== '') {
            const phoneRegex = /^[\+]?[1-9][\d\-]{0,15}$/
            if (!phoneRegex.test(formData.value.phoneNumber.replace(/[\s\-\(\)]/g, ''))) {
                toastMessageRef.value?.showFail('Please enter a valid phone number.')
                submitting.value = false
                return
            }
        }
        const formDataObj = new FormData()
        formDataObj.append('contactName', formData.value.contactName)
        formDataObj.append('email', formData.value.email)
        formDataObj.append('phoneNumber', formData.value.phoneNumber)
        formDataObj.append('message', formData.value.message)
        formDataObj.append('token', await generateToken(cmsInfo.website.id.toString(), timestampStr))
        formDataObj.append('websiteId', cmsInfo.website.id.toString())
        formDataObj.append('timestamp', timestampStr)
        formDataObj.append('randomStr', randomStr)
        // 来源页面 URL（用于追溯留言提交位置）
        formDataObj.append('sourceUrl', typeof window !== 'undefined' ? window.location.href : '')

        try {
            const res = await useHttp("/nuxt/common/addUserConsultant", {
                method: 'POST',
                body: formDataObj,
                // 使用FormData时不要设置Content-Type，浏览器会自动设置正确的类型
                headers: {
                    // 移除Content-Type让浏览器自动设置
                }
            }) as Response<any>

            if (res.code === 200) {
                toastMessageRef.value?.showSuccess('Thank you for your inquiry! We will reply to you as soon as possible!')
            }
            formData.value.contactName = ''
            formData.value.email = ''
            formData.value.phoneNumber = ''
            formData.value.message = ''
        } catch (error:any) {
            //console.error('Error submitting form:', error)
            if (error.code === 401) {
                toastMessageRef.value?.showFail('Token is timeout, please refresh the page and try again.')
            } else {
                toastMessageRef.value?.showFail(error.msg)
            }
        } finally {
            submitting.value = false
        }
    }

    //-----------------------------------咨询输入框的吸顶效果-----------------------------------------------------
    import { onMounted, onBeforeUnmount, nextTick } from 'vue'
    const inquiryBoxRef = ref<HTMLElement | null>(null);

    let stickyInquiryBoxHandler: any = null;
    let headerHeight = 0;
    let productsSectionTop = 0;

    const getHeaderHeight = () => {
        // 尝试 header id, 否则默认 0
        const header = document.querySelector('header') || document.querySelector('.gi-header') || null
        return header ? header.offsetHeight : 0;
    };

    function updatePositions() {
        headerHeight = getHeaderHeight();
        // 找到Latest Products section的位置
        const productsSection = document.querySelector('.gi-bottom-full');
        if (productsSection) {
            const rect = productsSection.getBoundingClientRect();
            // 用相对于文档顶部的绝对位置
            productsSectionTop = rect.top + window.scrollY;
        }
    }

    function handleSidebarSticky() {
        const el = inquiryBoxRef.value;
        if (!el) return;
        updatePositions();

        const sidebar = el.parentElement; // .gi-sidebar
        if (!sidebar) return;
        const sidebarRect = sidebar.getBoundingClientRect();
        const sidebarTop = sidebarRect.top + window.scrollY;

        const scrollY = window.scrollY;

        // 基础吸顶偏移量：header + margin
        let stickyTop = headerHeight + 16;
        // 避让侧栏中 inquiry 上方的整个区块（含 Product Category 与其展开内容）
        const prevBlock = (el.previousElementSibling as HTMLElement | null);
        if (prevBlock) {
            const prevRect = prevBlock.getBoundingClientRect();
            const baseTop = stickyTop;
            // 若上方区块在视口内并与基准吸顶线有重叠，则将吸顶点放到上方区块底部之后
            const overlapsTop = prevRect.top <= baseTop && prevRect.bottom > 0;
            if (overlapsTop) {
                stickyTop = Math.max(baseTop, prevRect.bottom + 16);
            }
        }

        // 计算Inquiry组件底部，如果马上要与products部分重叠，需要absolute, 否则sticky
        const elHeight = el.offsetHeight;
        // 当前的inquiry盒子底部 scrollY+距离顶部+盒子高度
        const elBottomY = scrollY + el.getBoundingClientRect().top - sidebarRect.top + elHeight;

        if (scrollY + stickyTop + elHeight > productsSectionTop - 16) {
            // 到底部了，absolute固定在products上方
            el.style.position = 'absolute';
            el.style.top = (productsSectionTop - sidebarTop - elHeight - 16) + 'px';
            el.style.left = '0';
            el.style.right = '0';
            el.style.width = 'auto';
        } else if (scrollY > sidebarTop - stickyTop) {
            // sticky吸顶
            el.style.position = 'fixed';
            el.style.top = stickyTop+100 + 'px';
            el.style.left = sidebarRect.left + 'px';
            el.style.width = sidebarRect.width + 'px';
            el.style.right = 'auto';
        } else {
            // 初始普通布局
            el.style.position = '';
            el.style.top = '';
            el.style.left = '';
            el.style.width = '';
            el.style.right = '';
        }
    }

    onMounted(() => {
        nextTick(() => {
            updatePositions();
            stickyInquiryBoxHandler = () => handleSidebarSticky();
            window.addEventListener('scroll', stickyInquiryBoxHandler, { passive: true });
            window.addEventListener('resize', stickyInquiryBoxHandler);
        });
    });

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', stickyInquiryBoxHandler);
        window.removeEventListener('resize', stickyInquiryBoxHandler);
    });


</script>

<style scoped lang="scss">
    .gi-grid {
        display: grid;
        grid-template-columns: 3fr 1.2fr;
        gap: 30px;

        @media (max-width: 1024px) {
            grid-template-columns: 1fr;
        }
    }

    .gi-article {
        min-width: 0;
        :deep(hr) {
            margin-top:12px !important;
        }

        :deep(code) {
            font-family: 'Courier New', Courier, monospace;
            background-color: #f5f5f5;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 0.875em;
            color: #d63384;
        }

        :deep(pre) {
            background-color: #f5f5f5;
            border-radius: 6px;
            padding: 16px;
            overflow-x: auto;
            line-height: 1.6;
        }

        :deep(pre code) {
            background: none;
            padding: 0;
            border-radius: 0;
            font-size: 0.875em;
            color: #333;
        }
        .codehilite{
            margin-bottom: 12px;
        }
    }

    .gi-title {
        font-size: 2rem;
        font-weight: 600;
        color: #1f2937;
        line-height: 1.2;
        margin-bottom: 8px;
    }

    .gi-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #6b7280;
        font-size: 0.95rem;
        margin-bottom: 18px;
    }

    .gi-meta-dot {
        opacity: .7;
        font-size: 3rem;
        line-height: 1rem;
        margin-top: -0.5rem;
    }
    .gi-meta-item {
        font-weight: 600;
    }
    time{
        font-weight: 600;
    }

    .gi-hero {
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 18px;
        box-shadow: 0 10px 28px rgba(0, 0, 0, .08);

        img {
            width: 100%;
            height: 420px;
            object-fit: cover;
            display: block;
        }

        @media (max-width: 640px) {
            img {
                height: 220px;
            }
        }
    }

    .gi-bottom-full {
        margin-top: 48px;
        display: flex;
        flex-direction: column;
        gap: 40px;
        width: 100%;
    }

    .gi-subtitle {
        display: flex;
        align-items: center;
        gap: 14px;
        justify-content: center;
        font-size: 1.55rem;
        font-weight: 600;
        color: #059669;
        /* theme green */
        text-transform: uppercase;
        letter-spacing: .04em;
        margin-bottom: 28px;
    }

    .gi-subtitle::before,
    .gi-subtitle::after {
        content: '';
        flex: 0.2 1 auto;
        height: 3px;
        background: #059669;
        /* theme green */
        border-radius: 999px;
        display: block;
    }

    .gi-products-grid {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: 20px;

        @media (max-width: 1536px) {
            grid-template-columns: repeat(4, 1fr);
        }

        @media (max-width: 1280px) {
            grid-template-columns: repeat(3, 1fr);
        }

        @media (max-width: 900px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 500px) {
            grid-template-columns: 1fr;
        }
    }

    .gi-product-card {
        background: #fff;
        border: 1px solid #d5dbe3;
        border-radius: 12px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transition: transform .2s ease, box-shadow .2s ease;
    }

    .gi-product-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 32px rgba(5, 150, 105, 0.15);
    }

    .gi-product-img {
        height: 190px;
        background: #f3f6fb;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .gi-product-img img {
        width: 100%;
        height: 100%;
        object-fit: fill;
        display: block;
    }

    .gi-product-info {
        padding: 16px 18px 20px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-height: 160px;
    }

    .gi-product-title {
        font-weight: 600;
        color: #122343;
        font-size: 1.02rem;
        line-height: 1.3;
    }

    .gi-product-desc {
        color: #5f6b7e;
        font-size: .94rem;
        line-height: 1.45;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .gi-product-more {
        margin-top: auto;
        color: rgb(22, 163, 74);
        font-weight: 600;
        text-decoration: none;
        font-size: .95rem;
    }

    .gi-product-more:hover {
        text-decoration: underline;
    }

    .gi-articles-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;

        @media (max-width: 1200px) {
            grid-template-columns: 1fr;
        }
    }

    .gi-article-item {
        background: #fff;
        border: 1px solid #d5dbe3;
        border-radius: 12px;
        overflow: hidden;
        display: flex;
        transition: transform .2s ease, box-shadow .2s ease;
    }

    .gi-article-item:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(5, 150, 105, 0.12);
    }

    .gi-article-thumb {
        flex: 0 0 180px;
        background: #f3f6fb;
        display: flex;
        align-items: center;
        justify-content: center;
        aspect-ratio: 600 / 337;
    }

    .gi-article-thumb img {
        width: 100%;
        height: 100%;
        aspect-ratio: 600 / 337;
        object-fit: fill;
        display: block;
    }

    .gi-article-right {
        padding: 18px 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .gi-article-title {
        font-weight: 600;
        color: #0b1f3a;
        font-size: 1.05rem;
        line-height: 1.2rem;
        text-decoration: none;
    }

    .gi-article-title:hover {
        color: #04752e
    }

    .gi-article-desc {
        color: #5f6b7e;
        font-size: .95rem;
        line-height: 1.5;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .gi-article-more {
        margin-top: auto;
        color: rgb(22, 163, 74);
        font-size: 1.1rem;
        text-decoration: none;
    }

    .gi-article-more:hover {
        text-decoration: underline;
    }

    /* Sidebar */
    .gi-sidebar {
        position: relative;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .gi-sidebar-box {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, .06);
        overflow: hidden;
    }

    .gi-side-title {
        font-weight: 600;
        color: rgb(22, 163, 74);
        font-size: 1.1rem;
        padding: 14px 16px;
        border-bottom: 1px solid #e5e7eb;
        background: #f9fafb;
    }

    .gi-side-products {
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .gi-side-product {
        display: grid;
        grid-template-columns: 88px 1fr;
        gap: 10px;
        align-items: center;
    }

    .gi-side-thumb {
        width: 88px;
        height: 72px;
        background: #f3f4f6;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
        }
    }

    .gi-side-right .gi-side-name {
        font-weight: 600;
        color: #111827;
        text-decoration: none;
        display: inline-block;
        margin-bottom: 2px;
    }

    .gi-side-right .gi-side-desc {
        color: #6b7280;
        font-size: .92rem;
        line-height: 1.3;
    }

    .gi-form {
        padding: 14px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        input,
        textarea {
            width: 100%;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            padding: 10px 12px;
            outline: none;
            font-size: .95rem;
        }

        button {
            background: rgb(22, 163, 74);
            color: #fff;
            border: 0;
            padding: 10px 16px;
            font-weight: 600;
            cursor: pointer;
        }

        button:hover {
            background: #1ca34b;
        }
    }

    // Product Details Section
    .product-details {
        display: flex;
        gap: 30px;
        margin-bottom: 40px;

        @media (max-width: 768px) {
            flex-direction: column;
        }
    }


    .product-info {
        flex: 4;

        .product-specs-table {
            border-collapse: collapse;

            td {
                padding: 8px 0;
            }

            tr {
                border-bottom: 1px solid #eee;

                &:last-child {
                    border-bottom: none;
                }
            }
        }

        .product-actions {
            margin-top: 20px;

            .btn-primary {
                background-color: rgb(22, 163, 74);
                color: white;
                border: none;
                padding: 12px 24px;
                font-weight: 600;
                cursor: pointer;

                &:hover {
                    background-color: rgb(22, 163, 74);
                }
            }

            .contact-info {
                margin-top: 10px;
                display: flex;
                align-items: center;

                .contact-link {
                    color: rgb(22, 163, 74);
                    text-decoration: underline;
                    margin-left: 5px;
                }
            }
        }
    }

    // Product Description Section
    .product-description {
        margin-bottom: 40px;

        .description-box {
            background-color: #bde5d3;
            padding: 20px;
            border-radius: 4px;

            h2 {
                font-size: 36px;
                margin-bottom: 10px;
                font-weight: 600;
            }

            p {
                line-height: 1.6;
            }
        }
    }

    // Features Section
    .features-section {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 40px;

        @media (max-width: 768px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 480px) {
            grid-template-columns: 1fr;
        }

        .feature {
            border: 1px solid #eee;
            padding: 15px;

            h3 {
                font-size: 18px;
                margin-bottom: 10px;
                font-weight: 600;
            }

            img {
                width: 100%;
                height: 150px;
                object-fit: cover;
                margin-bottom: 10px;
            }

            p {
                font-size: 14px;
                line-height: 1.4;
            }
        }
    }

    // Options Sections
    .options-section {
        margin-bottom: 40px;

        .option-category {
            margin-bottom: 30px;

            h3 {
                font-size: 18px;
                margin-bottom: 15px;
                font-weight: 600;
                position: relative;

                &:before {
                    content: '';
                    display: inline-block;
                    width: 10px;
                    height: 10px;
                    background-color: rgb(22, 163, 74);
                    margin-right: 10px;
                }
            }

            .option-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 15px;

                @media (max-width: 768px) {
                    grid-template-columns: repeat(2, 1fr);
                }

                .option-item {
                    border: 1px solid #ddd;
                    height: 120px;
                }
            }
        }
    }

    .category {
        width: 100%;
        background-color: #fff;
        color: #1f2937;
        padding: 0.75rem 0.75rem;
        border-radius: 0.375rem;
        border: 1px solid #d1d5db;
        transition: background-color 0.2s;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        &:hover {
            background-color: #f9fafb;
        }

        &.show-dropdown-btn {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        p {
            margin: 0;
            font-weight: 600;
            color: rgb(22, 163, 74);
            font-size: 1.1rem;
        }

        img {
            width: 1.875rem;
            height: 1.875rem;
        }
    }

    .category-wrapper {
        position: relative;
        width: 100%;
    }

    .dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        background-color: #fff;
        border: 1px solid #e5e7eb;
        border-top: none;
        border-radius: 0 0.375rem 0.375rem;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        z-index: 50;
    }

    .first-level-menu {
        border-bottom: 1px solid #f3f4f6;

        &:last-child {
            border-bottom: none;
        }
    }

    .first-level-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 0.75rem;
        cursor: pointer;

        &:hover {
            background-color: #f9fafb;
        }
    }

    .first-level-text {
        font-weight: 400;
        color: #1f2937;
    }

    .arrow-icon {
        width: 1rem;
        height: 1rem;
        transition: transform 0.2s ease;
    }

    .arrow-rotated {
        transform: rotate(180deg);
    }

    .submenu-container {
        padding-left: 1.5rem;
        padding-right: 0.75rem;
        background-color: #f9fafb;
    }

    .second-level-menu {
        padding: 0.25rem 0;

        &:hover {
            color: rgb(5, 150, 105);
        }
    }

    .sub-link {
        font-size: 0.875rem;
        color: #374151;
        text-decoration: none;

        &:hover {
            color: rgb(5, 150, 105);
            font-weight: 600;
        }

        &.active {
            color: rgb(22, 163, 74);
            font-weight: 600;
        }
    }

    .dropdown-enter-active,
    .dropdown-leave-active {
        transition: all 0.3s ease;
    }

    .dropdown-enter-from,
    .dropdown-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }

    .sticky-inquiry-box {
        z-index: 100;
    }

    .gi-theme-border:focus {
        --tw-ring-color: rgb(22, 163, 74) !important;
        outline: none;
    }

    .gi-btn-submitting {
        opacity: 0.7;
        cursor: not-allowed;
    }

    
</style>

